import { Smiley, SmileyMeh, SmileySad } from '@phosphor-icons/react';
import { useState } from 'react';
import { hapticsImpactLight } from '../../../utils/haptics';
import { useSnackbar } from 'notistack';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { addUserFeedback } from '../../../services/feedback';
import { FeedbackTrigger } from '../../../types/Feedback';
import { updateCurrentUserInfo } from '../../../services/auth';
import { X } from 'react-feather';

interface OraFeedbackProps {
  trigger: FeedbackTrigger | null;
  setOraFeedbackModalIsOpen: (isOpen: boolean) => void;
}

const OraFeedback: React.FC<OraFeedbackProps> = ({
  trigger,
  setOraFeedbackModalIsOpen,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);

  const [feedbackRating, setFeedbackRating] = useState<number | undefined>(
    undefined
  );
  const [writtenFeedback, setWrittenFeedback] = useState<string>('');

  const { enqueueSnackbar } = useSnackbar();

  const handleSubmitFeedback = async () => {
    if (!currentUser || !trigger) {
      setOraFeedbackModalIsOpen(false);
    } else {
      try {
        await addUserFeedback(
          currentUser.id,
          trigger,
          feedbackRating,
          writtenFeedback
        );
      } catch (error) {
        console.error('Error submitting feedback:', error);
      }

      if (trigger !== FeedbackTrigger.ELECTIVE) {
        try {
          await updateCurrentUserInfo({
            lastFeedbackTrigger: trigger,
          });
        } catch (error) {
          console.error('Error updating user info:', error);
        }
      }

      setOraFeedbackModalIsOpen(false);
      enqueueSnackbar('Thank you for your feedback!', {
        autoHideDuration: 3000,
      });
    }
  };

  const handleSkipFeedback = async () => {
    if (!currentUser || !trigger) {
      setOraFeedbackModalIsOpen(false);
    } else {
      if (trigger !== FeedbackTrigger.ELECTIVE) {
        try {
          await addUserFeedback(currentUser.id, trigger, undefined, '', true);
        } catch (error) {
          console.error('Error adding user feedback:', error);
        }

        try {
          await updateCurrentUserInfo({
            lastFeedbackTrigger: trigger,
          });
        } catch (error) {
          console.error('Error updating user info:', error);
        }
      }
      setOraFeedbackModalIsOpen(false);
    }
  };

  const renderPlaceholder = () => {
    if (feedbackRating === 1) {
      return 'What could we do better?';
    } else if (feedbackRating === 3) {
      return 'What could we do better?';
    } else if (feedbackRating === 5) {
      return 'What do you like most about Ora?';
    } else {
      return 'Share your feedback...';
    }
  };

  return (
    <div
      className="ora-feedback"
      style={
        trigger === FeedbackTrigger.ELECTIVE
          ? {
              top: '50%',
              left: '50%',
              bottom: 'auto',
              right: 'auto',
              transform: 'translate(-50%, -50%)',
            }
          : {}
      }
    >
      <h2 className="m-t-0">How is your Ora experience so far?</h2>
      <div className="rating-wrapper">
        <button
          className={`button button--icon-only button--feedback-rating button--feedback-rating--negative ${
            feedbackRating === 1 ? 'active' : ''
          }`}
          onClick={() => {
            setFeedbackRating(1);
            hapticsImpactLight();
          }}
          tabIndex={0}
          aria-label="Rate experience as negative"
        >
          <SmileySad />
        </button>
        <button
          className={`button button--icon-only button--feedback-rating button--feedback-rating--neutral ${
            feedbackRating === 3 ? 'active' : ''
          }`}
          onClick={() => {
            setFeedbackRating(3);
            hapticsImpactLight();
          }}
          tabIndex={0}
          aria-label="Rate experience as neutral"
        >
          <SmileyMeh />
        </button>
        <button
          className={`button button--icon-only button--feedback-rating button--feedback-rating--positive ${
            feedbackRating === 5 ? 'active' : ''
          }`}
          onClick={() => {
            setFeedbackRating(5);
            hapticsImpactLight();
          }}
          tabIndex={0}
          aria-label="Rate experience as positive"
        >
          <Smiley />
        </button>
      </div>
      {feedbackRating && (
        <div className="feedback-wrapper">
          <textarea
            placeholder={renderPlaceholder()}
            className="feedback-textarea"
            value={writtenFeedback}
            onChange={(e) => {
              setWrittenFeedback(e.target.value);
            }}
            tabIndex={0}
          />
        </div>
      )}
      {feedbackRating && (
        <button
          className="button button--secondary"
          onClick={() => {
            hapticsImpactLight();
            handleSubmitFeedback();
          }}
          tabIndex={0}
          aria-label="Submit feedback"
        >
          Submit
        </button>
      )}
      <button
        className="button button--icon-only button--close"
        onClick={() => {
          handleSkipFeedback();
          hapticsImpactLight();
          setOraFeedbackModalIsOpen(false);
        }}
        tabIndex={0}
        aria-label={
          trigger === FeedbackTrigger.ELECTIVE
            ? 'Cancel feedback'
            : 'Skip feedback for now'
        }
      >
        <X />
      </button>
    </div>
  );
};

export default OraFeedback;
